import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const Privacy: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-yellow-50 py-12">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 space-y-8">
          <div>
            <h1 className="text-3xl font-elegant font-bold text-secondary-900 mb-2">Privacy Policy</h1>
            <p className="text-sm text-secondary-500">Last updated: April 29, 2026</p>
          </div>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-secondary-800">Overview</h2>
            <p className="text-secondary-700 leading-relaxed">
              SoundX724 Music Collective ("we", "us", or "our") is committed to protecting your privacy.
              This policy explains what information we collect, how we use it, and what we do not do with it.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-secondary-800">Information We Collect</h2>
            <ul className="list-disc list-inside space-y-2 text-secondary-700 leading-relaxed">
              <li><strong>Email address</strong> — collected when you purchase or book tickets to our events.</li>
              <li><strong>Name</strong> — collected for ticket personalization and event check-in purposes.</li>
              <li><strong>Payment information</strong> — processed securely through Stripe. We do not store card details on our servers.</li>
              <li><strong>Usage data</strong> — basic analytics (e.g. pages visited) to help us improve the website. This data is not linked to your identity.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-secondary-800">How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-2 text-secondary-700 leading-relaxed">
              <li>To send you your event tickets and booking confirmations.</li>
              <li>To communicate important updates about events you have registered for.</li>
              <li>To process refunds or cancellations when requested.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-secondary-800">What We Do Not Do</h2>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800 leading-relaxed">
              We do <strong>not</strong> sell, rent, trade, or otherwise share your personal information —
              including your email address — with any third parties for marketing or commercial purposes.
              Your email is used solely for ticket delivery and event-related communications.
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-secondary-800">Cookies</h2>
            <p className="text-secondary-700 leading-relaxed">
              We use cookies to maintain your session and understand how visitors interact with our site.
              You may decline cookies via the notice shown on your first visit; however, some site features
              (such as purchasing tickets) may not function without them.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-secondary-800">Third-Party Services</h2>
            <p className="text-secondary-700 leading-relaxed">
              We use <strong>Stripe</strong> for payment processing. Stripe operates under its own privacy policy
              and is compliant with PCI DSS standards. We do not share your data with any other third-party service.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-secondary-800">Data Retention</h2>
            <p className="text-secondary-700 leading-relaxed">
              We retain your personal information only as long as necessary to fulfill the purposes outlined
              in this policy, or as required by applicable law.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-secondary-800">Your Rights</h2>
            <p className="text-secondary-700 leading-relaxed">
              You may request access to, correction of, or deletion of your personal data at any time by
              contacting us at{' '}
              <a href="mailto:soundx724@gmail.com" className="text-primary-600 underline hover:text-primary-800">
                soundx724@gmail.com
              </a>.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-secondary-800">Contact</h2>
            <p className="text-secondary-700 leading-relaxed">
              If you have any questions about this Privacy Policy, please contact us at{' '}
              <a href="mailto:soundx724@gmail.com" className="text-primary-600 underline hover:text-primary-800">
                soundx724@gmail.com
              </a>.
            </p>
          </section>

          <div className="pt-4 border-t border-secondary-200">
            <Link to="/" className="text-primary-600 hover:text-primary-800 text-sm font-medium">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
